import { ReactElement } from 'react'

import { Icon } from '../Icon'

/**
 * Ações padronizadas do painel. Cada chave representa uma intenção semântica
 * (o que o botão faz), não uma aparência — a aparência vem daqui.
 */
export type ActionKind =
  | 'edit'
  | 'delete'
  | 'create'
  | 'copy'
  | 'duplicate'
  | 'view'
  | 'download'
  | 'print'
  | 'upload'
  | 'send'
  | 'approve'
  | 'reject'
  | 'validate'
  | 'invalidate'
  | 'refund'
  | 'refresh'
  | 'settings'
  | 'external'
  | 'link'
  | 'unlink'
  | 'info'
  | 'save'
  | 'cancel'
  | 'clear'
  | 'lock'
  | 'unlock'
  | 'qrcode'
  | 'mark-paid'
  | 'unmark-paid'
  | 'sign'
  | 'conclude'
  | 'deactivate'
  | 'activate'
  | 'move-up'
  | 'move-down'

export interface IActionConfig {
  /** Ícone renderizado dentro do botão. Hoje LineIcons; trocar de biblioteca é editar só este arquivo. */
  icon: ReactElement
  colorScheme: string
  /** Rótulo pt-BR usado no tooltip, no aria-label e no texto quando `showLabel` está ligado. */
  label: string
  variant?: string
}

export const ACTIONS: Record<ActionKind, IActionConfig> = {
  edit: { icon: <Icon name="lni lni-pencil" />, colorScheme: 'blue', label: 'Editar' },
  delete: { icon: <Icon name="lni lni-trash-can" />, colorScheme: 'red', label: 'Excluir' },
  create: { icon: <Icon name="lni lni-plus" />, colorScheme: 'magenta', label: 'Adicionar' },
  // `copy` e `duplicate` são ações distintas de propósito: antes ambas usavam lni-clipboard.
  copy: { icon: <Icon name="lni lni-clipboard" />, colorScheme: 'gray', label: 'Copiar', variant: 'outline' },
  duplicate: { icon: <Icon name="lni lni-files" />, colorScheme: 'blue', label: 'Criar cópia' },
  view: { icon: <Icon name="lni lni-eye" />, colorScheme: 'gray', label: 'Ver detalhes' },
  download: { icon: <Icon name="lni lni-download" />, colorScheme: 'green', label: 'Baixar' },
  print: { icon: <Icon name="lni lni-printer" />, colorScheme: 'blue', label: 'Imprimir' },
  upload: { icon: <Icon name="lni lni-upload" />, colorScheme: 'blue', label: 'Enviar arquivo' },
  send: { icon: <Icon name="lni lni-envelope" />, colorScheme: 'blue', label: 'Enviar' },
  approve: { icon: <Icon name="lni lni-checkmark" />, colorScheme: 'abraGreen', label: 'Aprovar' },
  reject: { icon: <Icon name="lni lni-close" />, colorScheme: 'red', label: 'Reprovar' },
  // `approve`/`reject` são parecer sobre o conteúdo; `validate`/`invalidate` são a
  // conferência da secretaria, que pode ir e voltar. Ações distintas, cores distintas.
  validate: { icon: <Icon name="lni lni-checkmark-circle" />, colorScheme: 'abraGreen', label: 'Validar' },
  invalidate: { icon: <Icon name="lni lni-cross-circle" />, colorScheme: 'red', label: 'Invalidar' },
  // Estorno é dinheiro saindo: laranja para não se confundir com exclusão (vermelho)
  // nem com uma ação neutra. Antes era azul em duas telas e vermelho em outras duas.
  refund: { icon: <Icon name="lni lni-coin" />, colorScheme: 'orange', label: 'Reembolsar' },
  refresh: { icon: <Icon name="lni lni-reload" />, colorScheme: 'blue', label: 'Atualizar' },
  settings: { icon: <Icon name="lni lni-cog" />, colorScheme: 'purple', label: 'Configurações' },
  external: { icon: <Icon name="lni lni-world" />, colorScheme: 'magenta', label: 'Ver no site' },
  link: { icon: <Icon name="lni lni-link" />, colorScheme: 'gray', label: 'Copiar link', variant: 'outline' },
  // Desfaz uma associação com outro sistema (hoje o ticket do ABRA Park). Vermelho porque
  // desliga uma integração — mas `ghost`, porque é uma ação de dentro de um formulário e não
  // se aplica até o Salvar, diferente do `delete`.
  unlink: { icon: <Icon name="lni lni-unlink" />, colorScheme: 'red', label: 'Desvincular', variant: 'ghost' },
  info: { icon: <Icon name="lni lni-information" />, colorScheme: 'gray', label: 'Informações', variant: 'ghost' },
  save: { icon: <Icon name="lni lni-save" />, colorScheme: 'magenta', label: 'Salvar' },
  cancel: { icon: <Icon name="lni lni-close" />, colorScheme: 'gray', label: 'Cancelar', variant: 'ghost' },
  // Volta um campo ao valor padrão, ao lado dele. Não cancela nada: a mudança vai no Salvar.
  clear: { icon: <Icon name="lni lni-close" />, colorScheme: 'gray', label: 'Limpar', variant: 'ghost' },
  lock: { icon: <Icon name="lni lni-lock-alt" />, colorScheme: 'yellow', label: 'Restringir acesso' },
  unlock: { icon: <Icon name="lni lni-unlock" />, colorScheme: 'abraGreen', label: 'Liberar acesso' },
  'mark-paid': { icon: <Icon name="lni lni-coin" />, colorScheme: 'abraGreen', label: 'Marcar como pago' },
  'unmark-paid': { icon: <Icon name="lni lni-reply" />, colorScheme: 'gray', label: 'Desfazer pagamento' },
  // Assinatura de um documento no próprio painel (a RP das Vagas de Emprego): o parecer final
  // que põe o registro em vigor, por isso o verde do `approve` com o lápis da assinatura.
  sign: { icon: <Icon name="lni lni-pencil-alt" />, colorScheme: 'abraGreen', label: 'Assinar' },
  // Encerra um registro em andamento por conclusão (a vaga preenchida), e não por cancelamento.
  conclude: { icon: <Icon name="lni lni-checkmark-circle" />, colorScheme: 'blue', label: 'Concluir' },
  // Tira um registro de uso sem apagá-lo, e o põe de volta (o cargo, o colaborador).
  deactivate: { icon: <Icon name="lni lni-pause" />, colorScheme: 'orange', label: 'Desativar' },
  activate: { icon: <Icon name="lni lni-play" />, colorScheme: 'abraGreen', label: 'Ativar' },
  // Reordena um item de uma lista do formulário (módulos, bônus, professores). Neutro e `ghost`:
  // mudar a ordem não grava nada até o Salvar.
  'move-up': {
    icon: <Icon name="lni lni-chevron-up" />,
    colorScheme: 'gray',
    label: 'Mover para cima',
    variant: 'ghost'
  },
  'move-down': {
    icon: <Icon name="lni lni-chevron-down" />,
    colorScheme: 'gray',
    label: 'Mover para baixo',
    variant: 'ghost'
  },
  // LineIcons 4.0 não tem ícone de QR Code — mantém o PNG já usado no projeto.
  qrcode: {
    icon: <img src="/img/icons/qr-code.png" alt="" width={18} height={18} />,
    colorScheme: 'gray',
    label: 'QR Code'
  }
}

/** Ações destrutivas: recebem tratamento de cor próprio em menus. */
export const DESTRUCTIVE_ACTIONS: ActionKind[] = ['delete', 'reject', 'invalidate']
